/**
 * Entrada Page - Gerenciamento de Entradas de Estoque
 */

import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { PermissionGate } from "@/components/PermissionGate";
import { EntradasTable } from "@/components/entrada/EntradasTable";
import { EntradaSimplificadaDialog } from "@/components/entrada/EntradaSimplificadaDialog";
import { EntradaViewDialog } from "@/components/entrada/EntradaViewDialog";
import { EntradaDeleteDialog } from "@/components/entrada/EntradaDeleteDialog";
import { Plus, Search, X } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { entradaAdapter } from "@/data";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { EntradaCompleta } from "@/types/entrada";
import { toast } from "@/hooks/use-toast";

type SortField = "data_entrada" | "produto_nome" | "quantidade_compra" | "total_geral_documento" | "fornecedor_nome";
type SortDirection = "asc" | "desc" | null;

function EntradaPage() {
  const [search, setSearch] = useState("");
  const [entradas, setEntradas] = useState<EntradaCompleta[]>([]);
  const [tipoFilter, setTipoFilter] = useState<string>("all");
  const [isLoading, setIsLoading] = useState(true);
  const [isCabecalhoDialogOpen, setIsCabecalhoDialogOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedEntrada, setSelectedEntrada] = useState<EntradaCompleta | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [entradaToEdit, setEntradaToEdit] = useState<EntradaCompleta | null>(null);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  useEffect(() => {
    loadEntradas();
  }, []);

  const loadEntradas = async () => {
    try {
      setIsLoading(true);
      const response = await entradaAdapter.list({
        limit: 100,
      });
      setEntradas(response.items);
    } catch (error) {
      console.error("Error loading entradas:", error);
      setEntradas([]); // Garante UI consistente
      
      // Toast APENAS para erros REAIS (RLS, network, etc.)
      if (error && typeof error === 'object') {
        const errorMessage = (error as any)?.message || '';
        const errorCode = (error as any)?.code || '';
        
        // Ignora "banco vazio" ou resultados vazios normais
        if (
          !errorMessage.toLowerCase().includes('no rows') &&
          !errorMessage.toLowerCase().includes('empty') &&
          errorCode !== 'PGRST116' // Supabase: no rows
        ) {
          toast({
            title: "Erro ao carregar entradas",
            description: errorMessage || "Não foi possível carregar a lista de entradas.",
            variant: "destructive",
          });
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortDirection(null);
        setSortField(null);
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortItems = (items: EntradaCompleta[]) => {
    if (!sortField || !sortDirection) return items;

    return [...items].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case "data_entrada":
          aValue = new Date(a.cabecalho.data_entrada).getTime();
          bValue = new Date(b.cabecalho.data_entrada).getTime();
          break;
        case "produto_nome":
          aValue = a.itens[0]?.produto_nome?.toLowerCase() || "";
          bValue = b.itens[0]?.produto_nome?.toLowerCase() || "";
          break;
        case "quantidade_compra":
          aValue = a.itens[0]?.quantidade_compra || 0;
          bValue = b.itens[0]?.quantidade_compra || 0;
          break;
        case "total_geral_documento":
          aValue = a.totais.total_geral_documento || 0;
          bValue = b.totais.total_geral_documento || 0;
          break;
        case "fornecedor_nome":
          aValue = a.cabecalho.fornecedor_nome?.toLowerCase() || "";
          bValue = b.cabecalho.fornecedor_nome?.toLowerCase() || "";
          break;
        default:
          return 0;
      }

      if (!aValue && aValue !== 0) return 1;
      if (!bValue && bValue !== 0) return -1;

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  const filteredEntradas = entradas.filter((e) => {
    const matchesSearch =
      e.cabecalho.fornecedor_nome?.toLowerCase().includes(search.toLowerCase()) ||
      e.cabecalho.documento_numero?.toLowerCase().includes(search.toLowerCase()) ||
      e.itens.some((item) => item.produto_nome?.toLowerCase().includes(search.toLowerCase())) ||
      false;

    const matchesTipo = tipoFilter === "all" ? true : e.cabecalho.tipo_movimento === tipoFilter;

    return matchesSearch && matchesTipo;
  });

  const sortedEntradas = sortItems(filteredEntradas);

  const handleOpenCreate = () => {
    setIsCabecalhoDialogOpen(true);
  };

  const handleView = (entrada: EntradaCompleta) => {
    setSelectedEntrada(entrada);
    setIsViewOpen(true);
  };

  const handleDelete = (entrada: EntradaCompleta) => {
    setSelectedEntrada(entrada);
    setIsDeleteOpen(true);
  };

  const handleEdit = (entrada: EntradaCompleta) => {
    setEntradaToEdit(entrada);
    setIsEditDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedEntrada) return;

    try {
      setIsDeleting(true);
      console.log("🗑️ Deletando entrada:", selectedEntrada);
      await entradaAdapter.delete(selectedEntrada.id);
      toast({
        title: "✅ Entrada excluída",
        description: "A entrada foi removida com sucesso.",
      });
      await loadEntradas();
      setIsDeleteOpen(false);
    } catch (error: any) {
      console.error("❌ Erro ao excluir entrada:", error);
      
      let errorMessage = "Não foi possível excluir a entrada.";
      
      if (error?.message?.includes("policy") || error?.code === "42501") {
        errorMessage = "Você não tem permissão para excluir entradas.";
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro ao excluir entrada",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Entrada no Estoque</h1>
            <p className="text-muted-foreground">Gerencie compras, bonificações e ajustes</p>
          </div>
          <PermissionGate resource="stock_entries" action="create">
            <Button onClick={handleOpenCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Produto
            </Button>
          </PermissionGate>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por fornecedor, documento..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>

              <Select value={tipoFilter} onValueChange={setTipoFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo de movimento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  <SelectItem value="compra">Compra</SelectItem>
                  <SelectItem value="bonificacao">Bonificação</SelectItem>
                  <SelectItem value="devolucao_cliente">Devolução Cliente</SelectItem>
                  <SelectItem value="ajuste_positivo">Ajuste Positivo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(tipoFilter !== "all" || search) && (
              <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground flex-wrap">
                <span>Filtros ativos:</span>
                {tipoFilter !== "all" && (
                  <Badge variant="secondary" className="gap-2 pl-3 pr-1.5 py-1.5">
                    <span className="text-sm">Tipo: {tipoFilter}</span>
                    <button
                      onClick={() => setTipoFilter("all")}
                      className="ml-1 hover:bg-muted/80 rounded-full p-1
                                 min-w-[28px] min-h-[28px] flex items-center justify-center
                                 transition-colors hover:text-destructive touch-manipulation"
                      aria-label="Remover filtro de tipo"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </Badge>
                )}
                {search && (
                  <Badge variant="secondary" className="gap-2 pl-3 pr-1.5 py-1.5">
                    <span className="text-sm">Busca: "{search}"</span>
                    <button
                      onClick={() => setSearch("")}
                      className="ml-1 hover:bg-muted/80 rounded-full p-1
                                 min-w-[28px] min-h-[28px] flex items-center justify-center
                                 transition-colors hover:text-destructive touch-manipulation"
                      aria-label="Limpar busca"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </Badge>
                )}
                <button
                  onClick={() => {
                    setTipoFilter("all");
                    setSearch("");
                  }}
                  className="text-primary hover:underline ml-auto"
                >
                  Limpar todos
                </button>
              </div>
            )}

            <div className="text-sm text-muted-foreground mt-4">
              Exibindo <span className="font-medium">{filteredEntradas.length}</span> de{" "}
              <span className="font-medium">{entradas.length}</span> entradas
            </div>
          </CardContent>
        </Card>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" text="Carregando entradas..." />
          </div>
        ) : (
          <EntradasTable
            entradas={sortedEntradas}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />
        )}
      </div>

      {/* Dialogs */}
      <EntradaSimplificadaDialog
        open={isCabecalhoDialogOpen}
        onOpenChange={setIsCabecalhoDialogOpen}
        mode="create"
        entrada={null}
        onSuccess={loadEntradas}
      />
      <EntradaViewDialog
        open={isViewOpen}
        onOpenChange={setIsViewOpen}
        entrada={selectedEntrada}
        onEdit={() => {
          if (selectedEntrada) {
            setIsViewOpen(false);
            setEntradaToEdit(selectedEntrada);
            setIsEditDialogOpen(true);
          }
        }}
      />
      <EntradaDeleteDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        entrada={selectedEntrada}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
      <EntradaSimplificadaDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        mode="edit"
        entrada={entradaToEdit}
        onSuccess={() => {
          loadEntradas();
          setIsEditDialogOpen(false);
        }}
      />
    </Layout>
  );
}

export default function Entrada() {
  return <EntradaPage />;
}
